import React, { useCallback, useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Flame, Sparkles, Check } from 'lucide-react';
import { useClickSound } from '../../hooks/useClickSound';
import { useTTS } from '../../hooks/useTTS';
import { TTS_TEXTS } from '../../config/prompts';
interface BeliefDetoxStepProps {
  onComplete: () => void;
  onClose?: () => void;
}
export function BeliefDetoxStep({
  onComplete,
  onClose
}: BeliefDetoxStepProps) {
  const [progress, setProgress] = useState(0);
  const [isHolding, setIsHolding] = useState(false);
  const [isBurned, setIsBurned] = useState(false);
  const {
    playClick,
    playBurn,
    playSuccess
  } = useClickSound();
  const {
    speak,
    stop: stopTTS
  } = useTTS();
  const holdTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const hasSpokeBeliefRef = useRef(false);
  const BELIEF_TEXT = 'Muszę tłumić swój głos i emocje, aby być akceptowanym.';
  const AFFIRMATION_TEXT = 'Mój głos ma znaczenie. Wyrażam siebie z łatwością i spokojem.';
  // Web Speech API — speak text in Polish
  const speakText = useCallback((text: string) => {
    if (typeof window === 'undefined' || !window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = 'pl-PL';
    utterance.rate = 0.85;
    utterance.pitch = 0.95;
    utterance.volume = 0.8;
    const voices = window.speechSynthesis.getVoices();
    const plVoice = voices.find(v => v.lang.startsWith('pl'));
    if (plVoice) utterance.voice = plVoice;
    window.speechSynthesis.speak(utterance);
  }, []);
  // Preload voices & speak belief on mount
  useEffect(() => {
    speak(TTS_TEXTS.ritual_step3_mind);

    // Po krótkiej przerwie przeczytaj instrukcję o spalaniu
    const timer = setTimeout(() => {
      speak(TTS_TEXTS.ritual_belief_burn);
    }, 4500);
    return () => {
      clearTimeout(timer);
      stopTTS();
    };
  }, []);
  // Speak affirmation when card flips
  useEffect(() => {
    if (isBurned) {
      // Small delay to let the card animation start
      const timer = setTimeout(() => {
        speak(TTS_TEXTS.ritual_new_affirmation);
      }, 600);
      return () => clearTimeout(timer);
    }
  }, [isBurned]);
  useEffect(() => {
    if (isHolding && !isBurned) {
      playBurn();
      if (navigator.vibrate) navigator.vibrate(50);
      holdTimerRef.current = setInterval(() => {
        setProgress(prev => {
          if (prev >= 100) {
            setIsBurned(true);
            setIsHolding(false);
            playSuccess();
            if (navigator.vibrate) navigator.vibrate([100, 50, 100, 50, 200]);
            return 100;
          }
          if (navigator.vibrate) navigator.vibrate(50);
          return prev + 2; // 50 ticks to reach 100 (approx 2.5 seconds at 50ms interval)
        });
      }, 50);
    } else {
      if (holdTimerRef.current) {
        clearInterval(holdTimerRef.current);
      }
      if (!isBurned && progress > 0) {
        // Slowly decrease progress if let go early
        holdTimerRef.current = setInterval(() => {
          setProgress(prev => {
            if (prev <= 0) {
              if (holdTimerRef.current) clearInterval(holdTimerRef.current);
              return 0;
            }
            return prev - 5;
          });
        }, 50);
      }
    }
    return () => {
      if (holdTimerRef.current) clearInterval(holdTimerRef.current);
    };
  }, [isHolding, isBurned, playBurn, playSuccess]);
  const handlePointerDown = () => {
    if (!isBurned) {
      playClick();
      setIsHolding(true);
    }
  };
  const handlePointerUp = () => {
    setIsHolding(false);
  };
  const handleComplete = () => {
    playClick();
    onComplete();
  };
  return <motion.div className="fixed inset-0 z-50 flex flex-col items-center justify-between w-full h-full overflow-hidden bg-slate-900 text-white p-6" initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} data-id="element-962">
      <div className="w-full flex justify-between items-center mt-4 z-10" data-id="element-963">
        <div className="flex items-center space-x-3" data-id="element-964">
          {onClose && <button onClick={() => {
          playClick();
          onClose();
        }} className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white border border-white/20 backdrop-blur-md" data-id="element-965">
              ✕
            </button>}
          <div className="text-xs font-bold tracking-widest uppercase opacity-70" data-id="element-966">
            Krok 3: Umysł
          </div>
        </div>
        <div className="w-10 h-10" data-id="element-967"></div>
      </div>

      <div className="flex-1 flex flex-col items-center justify-center w-full relative perspective-1000" data-id="element-968">
        <AnimatePresence mode="wait" data-id="element-969">
          {!isBurned ? <motion.div key="negative" className="w-full max-w-sm bg-slate-800 rounded-3xl p-8 border border-slate-700 shadow-2xl relative overflow-hidden" initial={{
          rotateY: 90,
          opacity: 0
        }} animate={{
          rotateY: 0,
          opacity: 1,
          scale: isHolding ? 0.95 : 1,
          filter: `sepia(${progress}%) blur(${progress / 20}px) contrast(${1 + progress / 100})`
        }} exit={{
          scale: 0.8,
          opacity: 0,
          filter: 'blur(10px)'
        }} transition={{
          duration: 0.3
        }} data-id="element-970">
              <div className="absolute inset-0 bg-red-500/20 mix-blend-overlay" style={{
            opacity: progress / 100
          }} data-id="element-971" />
              <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-4 text-center" data-id="element-972">
                Rozpoznane Przekonanie
              </p>
              <h3 className="text-2xl font-medium text-center text-slate-200 mb-6" data-id="element-973">
                "{BELIEF_TEXT}"
              </h3>
              <div className="w-full h-1 bg-slate-700 rounded-full overflow-hidden" data-id="element-974">
                <div className="h-full bg-red-500 transition-all duration-75" style={{
              width: `${progress}%`
            }} data-id="element-975" />
              </div>
            </motion.div> : <motion.div key="positive" className="w-full max-w-sm bg-gradient-to-br from-zen-sage to-emerald-600 rounded-3xl p-8 border border-emerald-400/30 shadow-[0_0_40px_rgba(158,179,132,0.4)] relative overflow-hidden" initial={{
          scale: 0.8,
          opacity: 0,
          y: 20
        }} animate={{
          scale: 1,
          opacity: 1,
          y: 0
        }} transition={{
          type: 'spring',
          bounce: 0.5
        }} data-id="element-976">
              <div className="absolute top-0 right-0 -mt-4 -mr-4 w-24 h-24 bg-white/20 rounded-full blur-2xl" data-id="element-977" />
              <div className="flex justify-center mb-4" data-id="element-978">
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm" data-id="element-979">
                  <Sparkles className="w-6 h-6 text-white" data-id="element-980" />
                </div>
              </div>
              <p className="text-xs font-bold text-emerald-100 uppercase tracking-widest mb-2 text-center" data-id="element-981">
                Nowa Afirmacja
              </p>
              <h3 className="text-2xl font-medium text-center text-white mb-2 leading-tight" data-id="element-982">
                "{AFFIRMATION_TEXT}"
              </h3>
            </motion.div>}
        </AnimatePresence>
      </div>

      <div className="mb-10 z-10 w-full max-w-xs flex flex-col items-center space-y-6" data-id="element-983">
        {!isBurned ? <button onPointerDown={handlePointerDown} onPointerUp={handlePointerUp} onPointerLeave={handlePointerUp} onContextMenu={e => e.preventDefault()} className={`w-full rounded-full py-4 text-sm font-bold transition-all duration-200 flex items-center justify-center space-x-2 select-none touch-none ${isHolding ? 'bg-red-600 text-white scale-95 shadow-[0_0_30px_rgba(220,38,38,0.6)]' : 'bg-slate-800 text-slate-300 border border-slate-700 hover:bg-slate-700'}`} data-id="element-984">
            <Flame className={`w-5 h-5 ${isHolding ? 'animate-pulse' : ''}`} data-id="element-985" />
            <span data-id="element-986">{isHolding ? 'Spalanie...' : 'Przytrzymaj by SPALIĆ'}</span>
          </button> : <motion.button onClick={handleComplete} className="w-full bg-white text-emerald-900 rounded-full py-4 text-sm font-bold hover:bg-slate-100 transition-colors flex items-center justify-center space-x-2 shadow-lg" initial={{
        scale: 0.9,
        opacity: 0
      }} animate={{
        scale: 1,
        opacity: 1
      }} data-id="element-987">
            <Check className="w-5 h-5" data-id="element-988" />
            <span data-id="element-989">Zakończ Rytuał</span>
          </motion.button>}
      </div>
    </motion.div>;
}